/**
 * useOutreachKpis — the CRM Overview sub-tab's campaign KPI read (ruling 66).
 * Go caches the view for 15s per campaign, so a plain read can return a view
 * up to that old; the FE staleTime is longer because the tab refetches on SSE
 * (outreach_rate_agreed/outreach_entry_updated) anyway, plus a 60s poll so
 * the view stays fresh even while nothing has fired an event.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { OutreachKpis } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { fetchFreshOutreachKpis, fetchOutreachKpis } from '../api/outreachApi';

export function useOutreachKpis(campaignId: string) {
  return useQuery<OutreachKpis>({
    queryKey: campaignKeys.outreachKpis(campaignId),
    queryFn: () => fetchOutreachKpis(campaignId),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * Re-reads the campaign's KPIs past Go's 15s cache and stores them, replacing any read already in flight. Call it
 * after the caller's own write: a plain invalidate would refetch the cached, pre-write view. A failed read lands in the KPI query's own error state (the
 * Overview shows it) rather than failing the caller's already-successful write.
 */
export async function refreshOutreachKpisNow(queryClient: QueryClient, campaignId: string): Promise<void> {
  // prefetchQuery would join a read already in flight (started before the write, so pre-write); cancel it first.
  await queryClient.cancelQueries({ queryKey: campaignKeys.outreachKpis(campaignId) });
  await queryClient.prefetchQuery({
    queryKey: campaignKeys.outreachKpis(campaignId),
    queryFn: () => fetchFreshOutreachKpis(campaignId),
    staleTime: 0,
  });
}

/** Go's KPI cache lifetime (kpis.CacheTTL), used only when the view does not say its own (an older Go). */
export const OUTREACH_KPIS_DEFAULT_REFRESH_MS = 15_000;

/** How long to wait between live KPI refreshes: as long as Go said the last view stays cached. */
export function kpisRefreshIntervalMs(kpis: Pick<OutreachKpis, 'refreshAfterMs'> | undefined): number {
  return kpis?.refreshAfterMs ?? OUTREACH_KPIS_DEFAULT_REFRESH_MS;
}
